import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

// ─── BRAND COLORS ─────────────────────────────────────────────────────────────
const TEAL = [13, 184, 126];
const INK = [4, 18, 14];
const SURF = [245, 249, 247];

// ─── USERS EXPORT ─────────────────────────────────────────────────────────────

export function exportUsersExcel(users) {
  const rows = users.map((u, i) => ({
    "#": i + 1,
    "Full Name": u.name ?? "",
    "Email": u.email ?? "",
    "Status": u.subscriptionStatus ?? "",
    "Plan": u.subscription?.plan ?? "—",
    "Platform": u.subscription?.platform ?? "—",
    "Period End": u.subscription?.periodEnd ? new Date(u.subscription.periodEnd).toLocaleDateString() : "—",
    "Child Name": u.childProfile?.childName ?? "—",
    "Child Age (mo)": u.childProfile?.ageMonths ?? "—",
    "Challenges": (u.childProfile?.challenges ?? []).join(", ") || "—",
    "Joined": u.createdAt ? new Date(u.createdAt).toLocaleDateString() : "",
    "Last Login": u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleDateString() : "—",
  }));

  const ws = XLSX.utils.json_to_sheet(rows);
  ws["!cols"] = [{ wch: 4 }, { wch: 22 }, { wch: 30 }, { wch: 12 }, { wch: 10 }, { wch: 15 }, { wch: 14 }, { wch: 16 }, { wch: 14 }, { wch: 30 }, { wch: 12 }, { wch: 12 }];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Users");

  const summary = [
    ["Kunga Basics — User Export"],
    ["Generated:", new Date().toLocaleString()],
    ["Total:", rows.length],
    ["Active:", users.filter(u => u.subscriptionStatus === "ACTIVE").length],
    ["Trial:", users.filter(u => u.subscriptionStatus === "TRIAL").length],
    ["Free:", users.filter(u => u.subscriptionStatus === "NONE").length],
    ["Cancelled:", users.filter(u => u.subscriptionStatus === "CANCELLED").length],
    ["Scholarship:", users.filter(u => u.subscriptionStatus === "SCHOLARSHIP").length],
  ];
  const ws2 = XLSX.utils.aoa_to_sheet(summary);
  ws2["!cols"] = [{ wch: 20 }, { wch: 20 }];
  XLSX.utils.book_append_sheet(wb, ws2, "Summary");

  XLSX.writeFile(wb, `kunga-users-${new Date().toISOString().split("T")[0]}.xlsx`);
}

export function exportUsersCSV(users) {
  const header = "Name,Email,Status,Plan,Platform,Child,Age(mo),Joined";
  const rows = users.map(u =>
    [`"${u.name ?? ""}"`, `"${u.email}"`, u.subscriptionStatus ?? "",
    u.subscription?.plan ?? "—", u.subscription?.platform ?? "—",
    `"${u.childProfile?.childName ?? "—"}"`, u.childProfile?.ageMonths ?? "—",
    u.createdAt ? new Date(u.createdAt).toLocaleDateString() : ""].join(",")
  );
  _downloadText([header, ...rows].join("\n"), "kunga-users.csv", "text/csv");
}

export function exportUsersPDF(users, filters = {}) {
  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  _pdfHeader(doc, "User Report", filters);

  const statY = _pdfStats(doc, 25, [
    ["Total", users.length], ["Active", users.filter(u => u.subscriptionStatus === "ACTIVE").length],
    ["Trial", users.filter(u => u.subscriptionStatus === "TRIAL").length],
    ["Free", users.filter(u => u.subscriptionStatus === "NONE").length],
    ["Cancelled", users.filter(u => u.subscriptionStatus === "CANCELLED").length],
  ]);

  autoTable(doc, {
    startY: statY + 4,
    head: [["#", "Name", "Email", "Status", "Plan", "Platform", "Child", "Age", "Joined"]],
    body: users.map((u, i) => [
      i + 1, u.name ?? "", u.email ?? "", u.subscriptionStatus ?? "",
      u.subscription?.plan ?? "—", u.subscription?.platform ?? "—",
      u.childProfile?.childName ?? "—",
      u.childProfile?.ageMonths ? `${u.childProfile.ageMonths}mo` : "—",
      u.createdAt ? new Date(u.createdAt).toLocaleDateString() : "",
    ]),
    headStyles: { fillColor: TEAL, textColor: [255, 255, 255], fontStyle: "bold", fontSize: 8 },
    bodyStyles: { fontSize: 7.5 },
    alternateRowStyles: { fillColor: SURF },
    columnStyles: { 0: { cellWidth: 8 }, 1: { cellWidth: 36 }, 2: { cellWidth: 50 }, 3: { cellWidth: 20 }, 4: { cellWidth: 18 }, 5: { cellWidth: 26 }, 6: { cellWidth: 24 }, 7: { cellWidth: 12 }, 8: { cellWidth: 22 } },
    margin: { left: 14, right: 14 },
    didDrawPage: d => _pdfFooter(doc, d),
  });
  doc.save(`kunga-users-${_today()}.pdf`);
}

// ─── DONATIONS EXPORT ─────────────────────────────────────────────────────────

export function exportDonationsExcel(donations) {
  const rows = donations.map((d, i) => ({
    "#": i + 1,
    "Donor": d.donorName ?? "Anonymous",
    "Email": d.email ?? "",
    "Amount": `$${d.amountUsd}`,
    "Currency": d.currency ?? "USD",
    "Method": d.paymentMethod ?? "",
    "Campaign": d.campaign ?? "—",
    "Status": d.status ?? "",
    "Wall": d.showOnDonorWall ? "Yes" : "No",
    "Date": d.createdAt ? new Date(d.createdAt).toLocaleDateString() : "",
  }));

  const ws = XLSX.utils.json_to_sheet(rows);
  ws["!cols"] = [{ wch: 4 }, { wch: 22 }, { wch: 30 }, { wch: 10 }, { wch: 8 }, { wch: 14 }, { wch: 22 }, { wch: 12 }, { wch: 6 }, { wch: 12 }];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Donations");

  const total = donations.filter(d => d.status === "COMPLETED").reduce((a, d) => a + d.amountUsd, 0);
  const summary = [
    ["Kunga Basics — Donation Export"],
    ["Generated:", new Date().toLocaleString()],
    ["Total Donations:", rows.length],
    ["Total Amount:", `$${total.toFixed(2)}`],
    ["Completed:", donations.filter(d => d.status === "COMPLETED").length],
    ["Pending:", donations.filter(d => d.status === "PENDING").length],
    ["Failed:", donations.filter(d => d.status === "FAILED").length],
  ];
  const ws2 = XLSX.utils.aoa_to_sheet(summary);
  ws2["!cols"] = [{ wch: 20 }, { wch: 20 }];
  XLSX.utils.book_append_sheet(wb, ws2, "Summary");

  XLSX.writeFile(wb, `kunga-donations-${_today()}.xlsx`);
}

export function exportDonationsCSV(donations) {
  const header = "Donor,Email,Amount,Currency,Method,Campaign,Status,Donor Wall,Date";
  const rows = donations.map(d =>
    [`"${d.donorName ?? "Anonymous"}"`, `"${d.email ?? ""}"`,
    `$${d.amountUsd}`, d.currency ?? "USD", d.paymentMethod ?? "",
    `"${d.campaign ?? "—"}"`, d.status ?? "",
    d.showOnDonorWall ? "Yes" : "No",
    d.createdAt ? new Date(d.createdAt).toLocaleDateString() : ""].join(",")
  );
  _downloadText([header, ...rows].join("\n"), "kunga-donations.csv", "text/csv");
}

export function exportDonationsPDF(donations, filters = {}) {
  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  _pdfHeader(doc, "Donation Report", filters);

  const completed = donations.filter(d => d.status === "COMPLETED");
  const total = completed.reduce((a, d) => a + d.amountUsd, 0);

  const statY = _pdfStats(doc, 25, [
    ["Total", donations.length], ["Completed", completed.length],
    ["Pending", donations.filter(d => d.status === "PENDING").length],
    ["Failed", donations.filter(d => d.status === "FAILED").length],
    ["Total $", `$${total.toFixed(0)}`],
  ]);

  autoTable(doc, {
    startY: statY + 4,
    head: [["#", "Donor", "Email", "Amount", "Method", "Campaign", "Status", "Date"]],
    body: donations.map((d, i) => [
      i + 1, d.donorName ?? "Anonymous", d.email ?? "", `$${d.amountUsd}`,
      d.paymentMethod ?? "", d.campaign ?? "—", d.status ?? "",
      d.createdAt ? new Date(d.createdAt).toLocaleDateString() : "",
    ]),
    headStyles: { fillColor: TEAL, textColor: [255, 255, 255], fontStyle: "bold", fontSize: 8 },
    bodyStyles: { fontSize: 7.5 },
    alternateRowStyles: { fillColor: SURF },
    columnStyles: { 0: { cellWidth: 8 }, 1: { cellWidth: 32 }, 2: { cellWidth: 48 }, 3: { cellWidth: 18 }, 4: { cellWidth: 26 }, 5: { cellWidth: 36 }, 6: { cellWidth: 20 }, 7: { cellWidth: 22 } },
    margin: { left: 14, right: 14 },
    didDrawPage: d => _pdfFooter(doc, d),
  });
  doc.save(`kunga-donations-${_today()}.pdf`);
}

// ─── HELPERS ──────────────────────────────────────────────────────────────────

function _today() { return new Date().toISOString().split("T")[0]; }

function _downloadText(text, filename, type) {
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([text], { type }));
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
}

function _pdfHeader(doc, title, filters = {}) {
  doc.setFillColor(...INK);
  doc.rect(0, 0, 297, 22, "F");
  doc.setTextColor(...TEAL);
  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.text("🌿 Kunga Basics", 14, 10);
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.text(title, 14, 17);
  doc.setTextColor(150, 150, 150);
  doc.setFontSize(8);
  doc.text(`Generated: ${new Date().toLocaleString()}`, 180, 10);
  const filterStr = Object.entries(filters).filter(([, v]) => v).map(([k, v]) => `${k}: ${v}`).join("  |  ");
  if (filterStr) doc.text(filterStr, 180, 16);
}

function _pdfStats(doc, startY, stats) {
  let x = 14;
  doc.setFontSize(9);
  stats.forEach(([label, count]) => {
    doc.setFillColor(...SURF);
    doc.roundedRect(x, startY, 50, 12, 2, 2, "F");
    doc.setTextColor(...INK);
    doc.setFont("helvetica", "bold");
    doc.text(String(count), x + 25, startY + 5.5, { align: "center" });
    doc.setFont("helvetica", "normal");
    doc.setTextColor(100, 100, 100);
    doc.setFontSize(7);
    doc.text(label, x + 25, startY + 10, { align: "center" });
    doc.setFontSize(9);
    x += 54;
  });
  return startY + 12;
}

function _pdfFooter(doc, data) {
  doc.setFontSize(7);
  doc.setTextColor(150, 150, 150);
  doc.text(
    `Page ${doc.internal.getCurrentPageInfo().pageNumber} — Kunga Basics Admin Export`,
    14, doc.internal.pageSize.height - 6
  );
}

// ─── MOBILE MONEY EXPORT ──────────────────────────────────────────────────────

export function exportMobileMoneyExcel(transactions) {
  const rows = transactions.map((t, i) => ({
    "#": i + 1,
    "User Name": t.user?.name ?? "—",
    "Email": t.user?.email ?? "—",
    "Provider": t.mobileMoneyProvider ?? "—",
    "Phone": t.mobileMoneyPhone ?? "—",
    "Plan": t.plan ?? "—",
    "Status": t.status ?? "—",
    "Tx Ref": t.flutterwaveTxId ?? "—",
    "Period Start": t.periodStart ? new Date(t.periodStart).toLocaleDateString() : "—",
    "Period End": t.periodEnd ? new Date(t.periodEnd).toLocaleDateString() : "—",
    "Date": t.createdAt ? new Date(t.createdAt).toLocaleDateString() : "—",
  }));

  const ws = XLSX.utils.json_to_sheet(rows);
  ws["!cols"] = [{ wch: 4 }, { wch: 22 }, { wch: 30 }, { wch: 18 }, { wch: 18 }, { wch: 10 }, { wch: 12 }, { wch: 24 }, { wch: 14 }, { wch: 14 }, { wch: 12 }];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Mobile Money");

  const byProvider = {};
  transactions.forEach(t => { const p = t.mobileMoneyProvider ?? "Unknown"; byProvider[p] = (byProvider[p] || 0) + 1; });

  const summary = [
    ["Kunga Basics — Mobile Money Export"],
    ["Generated:", new Date().toLocaleString()],
    ["Total Transactions:", transactions.length],
    ["Active:", transactions.filter(t => t.status === "ACTIVE").length],
    ["Pending:", transactions.filter(t => t.status === "NONE").length],
    ["Failed:", transactions.filter(t => t.status === "CANCELLED").length],
    [],
    ["By Provider:"],
    ...Object.entries(byProvider).map(([p, c]) => [p, c]),
  ];
  const ws2 = XLSX.utils.aoa_to_sheet(summary);
  ws2["!cols"] = [{ wch: 22 }, { wch: 20 }];
  XLSX.utils.book_append_sheet(wb, ws2, "Summary");

  XLSX.writeFile(wb, `kunga-mobile-money-${_today()}.xlsx`);
}

export function exportMobileMoneyCSV(transactions) {
  const header = "User,Email,Provider,Phone,Plan,Status,Tx Ref,Period End,Date";
  const rows = transactions.map(t =>
    [`"${t.user?.name ?? "—"}"`, `"${t.user?.email ?? "—"}"`,
    t.mobileMoneyProvider ?? "—", t.mobileMoneyPhone ?? "—",
    t.plan ?? "—", t.status ?? "—",
    t.flutterwaveTxId ?? "—",
    t.periodEnd ? new Date(t.periodEnd).toLocaleDateString() : "—",
    t.createdAt ? new Date(t.createdAt).toLocaleDateString() : "—"].join(",")
  );
  _downloadText([header, ...rows].join("\n"), "kunga-mobile-money.csv", "text/csv");
}

export function exportMobileMoneyPDF(transactions, filters = {}) {
  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  _pdfHeader(doc, "Mobile Money Report", filters);

  const statY = _pdfStats(doc, 25, [
    ["Total", transactions.length],
    ["Active", transactions.filter(t => t.status === "ACTIVE").length],
    ["Pending", transactions.filter(t => t.status === "NONE").length],
    ["Failed", transactions.filter(t => t.status === "CANCELLED").length],
    ["M-Pesa", transactions.filter(t => t.mobileMoneyProvider === "mpesa").length],
  ]);

  autoTable(doc, {
    startY: statY + 4,
    head: [["#", "User", "Email", "Provider", "Phone", "Plan", "Status", "Period End", "Date"]],
    body: transactions.map((t, i) => [
      i + 1,
      t.user?.name ?? "—",
      t.user?.email ?? "—",
      t.mobileMoneyProvider ?? "—",
      t.mobileMoneyPhone ?? "—",
      t.plan ?? "—",
      t.status ?? "—",
      t.periodEnd ? new Date(t.periodEnd).toLocaleDateString() : "—",
      t.createdAt ? new Date(t.createdAt).toLocaleDateString() : "—",
    ]),
    headStyles: { fillColor: TEAL, textColor: [255, 255, 255], fontStyle: "bold", fontSize: 8 },
    bodyStyles: { fontSize: 7.5 },
    alternateRowStyles: { fillColor: SURF },
    columnStyles: { 0: { cellWidth: 8 }, 1: { cellWidth: 30 }, 2: { cellWidth: 46 }, 3: { cellWidth: 26 }, 4: { cellWidth: 28 }, 5: { cellWidth: 16 }, 6: { cellWidth: 18 }, 7: { cellWidth: 22 }, 8: { cellWidth: 20 } },
    margin: { left: 14, right: 14 },
    didDrawPage: d => _pdfFooter(doc, d),
  });
  doc.save(`kunga-mobile-money-${_today()}.pdf`);
}

// ─── SUBSCRIPTIONS EXPORT ─────────────────────────────────────────────────────

export function exportSubscriptionsExcel(subscriptions) {
  const rows = subscriptions.map((s, i) => ({
    "#": i + 1,
    "User Name": s.user?.name ?? "—",
    "Email": s.user?.email ?? "—",
    "Plan": s.plan ?? "—",
    "Platform": s.platform ?? "—",
    "Status": s.status ?? "—",
    "Period Start": s.periodStart ? new Date(s.periodStart).toLocaleDateString() : "—",
    "Period End": s.periodEnd ? new Date(s.periodEnd).toLocaleDateString() : "—",
    "Cancelled At": s.cancelledAt ? new Date(s.cancelledAt).toLocaleDateString() : "—",
    "Mobile Provider": s.mobileMoneyProvider ?? "—",
    "Mobile Phone": s.mobileMoneyPhone ?? "—",
  }));

  const ws = XLSX.utils.json_to_sheet(rows);
  ws["!cols"] = [{ wch: 4 }, { wch: 22 }, { wch: 30 }, { wch: 10 }, { wch: 18 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 18 }, { wch: 18 }];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Subscriptions");

  const summary = [
    ["Kunga Basics — Subscription Export"],
    ["Generated:", new Date().toLocaleString()],
    ["Total:", rows.length],
    ["Active:", subscriptions.filter(s => s.status === "ACTIVE").length],
    ["Trial:", subscriptions.filter(s => s.status === "TRIAL").length],
    ["Cancelled:", subscriptions.filter(s => s.status === "CANCELLED").length],
    ["Expired:", subscriptions.filter(s => s.status === "EXPIRED").length],
    ["Scholarship:", subscriptions.filter(s => s.status === "SCHOLARSHIP").length],
  ];
  const ws2 = XLSX.utils.aoa_to_sheet(summary);
  ws2["!cols"] = [{ wch: 22 }, { wch: 20 }];
  XLSX.utils.book_append_sheet(wb, ws2, "Summary");

  XLSX.writeFile(wb, `kunga-subscriptions-${_today()}.xlsx`);
}

export function exportSubscriptionsCSV(subscriptions) {
  const header = "Name,Email,Plan,Platform,Status,Period Start,Period End,Cancelled At";
  const rows = subscriptions.map(s =>
    [`"${s.user?.name ?? "—"}"`, `"${s.user?.email ?? "—"}"`,
    s.plan ?? "—", s.platform ?? "—", s.status ?? "—",
    s.periodStart ? new Date(s.periodStart).toLocaleDateString() : "—",
    s.periodEnd ? new Date(s.periodEnd).toLocaleDateString() : "—",
    s.cancelledAt ? new Date(s.cancelledAt).toLocaleDateString() : "—"].join(",")
  );
  _downloadText([header, ...rows].join("\n"), "kunga-subscriptions.csv", "text/csv");
}

export function exportSubscriptionsPDF(subscriptions, filters = {}) {
  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  _pdfHeader(doc, "Subscription Report", filters);

  const statY = _pdfStats(doc, 25, [
    ["Total", subscriptions.length],
    ["Active", subscriptions.filter(s => s.status === "ACTIVE").length],
    ["Trial", subscriptions.filter(s => s.status === "TRIAL").length],
    ["Cancelled", subscriptions.filter(s => s.status === "CANCELLED").length],
    ["Scholarship", subscriptions.filter(s => s.status === "SCHOLARSHIP").length],
  ]);

  autoTable(doc, {
    startY: statY + 4,
    head: [["#", "Name", "Email", "Plan", "Platform", "Status", "Period End", "Cancelled At"]],
    body: subscriptions.map((s, i) => [
      i + 1,
      s.user?.name ?? "—",
      s.user?.email ?? "—",
      s.plan ?? "—",
      s.platform ?? "—",
      s.status ?? "—",
      s.periodEnd ? new Date(s.periodEnd).toLocaleDateString() : "—",
      s.cancelledAt ? new Date(s.cancelledAt).toLocaleDateString() : "—",
    ]),
    headStyles: { fillColor: TEAL, textColor: [255, 255, 255], fontStyle: "bold", fontSize: 8 },
    bodyStyles: { fontSize: 7.5 },
    alternateRowStyles: { fillColor: SURF },
    columnStyles: { 0: { cellWidth: 8 }, 1: { cellWidth: 36 }, 2: { cellWidth: 50 }, 3: { cellWidth: 14 }, 4: { cellWidth: 24 }, 5: { cellWidth: 20 }, 6: { cellWidth: 22 }, 7: { cellWidth: 22 } },
    margin: { left: 14, right: 14 },
    didDrawPage: d => _pdfFooter(doc, d),
  });
  doc.save(`kunga-subscriptions-${_today()}.pdf`);
}
